import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { useLanguage } from './hooks/useLanguage';
import { LanguageToggle } from './components/LanguageToggle';
import { Hero } from './components/Hero';
import { Features } from './components/Features';
import { Demo } from './components/Demo';
import { DailyQuiz } from './components/DailyQuiz';
import { WhatsNew } from './components/WhatsNew';
import { Milestones } from './components/Milestones';
import { AboutUs } from './components/AboutUs';
import { SocialProof } from './components/SocialProof';
import { FAQ } from './components/FAQ';
import { Waitlist } from './components/Waitlist';
import { SEOHead } from './components/SEOHead';
import { Lanyard } from './components/Lanyard';
import './App.css';

function App() {
  const { currentLanguage, toggleLanguage, t } = useLanguage();
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [headerHeight, setHeaderHeight] = useState<number>(72);
  const [hudTop, setHudTop] = useState<number>(20);
  const [showMenu, setShowMenu] = useState(false);

  React.useEffect(() => {
    const el = document.querySelector('.header') as HTMLElement | null;
    if (!el) return;
    const update = () => setHeaderHeight(el.offsetHeight || 72);
    update();
    const ro = new (window as any).ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  React.useEffect(() => {
    // place HUD roughly at the vertical center of the header, slightly adjusted
    setHudTop(Math.max(10, Math.round(headerHeight * 0.5) - 6));
  }, [headerHeight]);

  const handleNavClick = (target: string) => {
    const el = document.getElementById(target);
    if (el) {
      const offset = headerHeight + 8;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setShowMenu(false);
  };

  return (
    <HelmetProvider>
      <div className="App">
        <SEOHead currentLanguage={currentLanguage} />
        
        <header className="header" role="banner">
          <div className="header-content">
            <div className="header-left">
              <button
                className={`menu-button ${showMenu ? 'open' : ''}`}
                aria-label="Toggle navigation menu"
                aria-expanded={showMenu}
                onClick={() => setShowMenu(!showMenu)}
              >
                <span className="menu-rings" aria-hidden="true"></span>
                <span className="menu-lines" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>

              <motion.div
                className="logo"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <a href="/" aria-label="AlphoraAI 首页">AlphoraAI</a>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <LanguageToggle
                currentLanguage={currentLanguage}
                onToggle={toggleLanguage}
              />
            </motion.div>
          </div>
        </header>

        <div className={`menu-overlay ${showMenu ? 'open' : ''}`} onClick={() => setShowMenu(false)}></div>
        <nav className={`menu-drawer ${showMenu ? 'open' : ''}`} aria-label="Primary">
          <div className="menu-drawer-inner">
            <div className="menu-drawer-header">
              <span className="menu-drawer-title">AlphoraAI</span>
              <button className="menu-close" aria-label="Close menu" onClick={() => setShowMenu(false)}>
                ×
              </button>
            </div>
            {t.nav.map((item) => (
              <button
                key={item.target + item.label}
                className="menu-link"
                onClick={() => handleNavClick(item.target)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>

        <Lanyard
          onUnlock={() => setShowQuizModal(true)}
          stickToTop
          topOffsetPx={headerHeight}
          cardAttachY={3.1}
          hudTopPx={hudTop}
          autoHideOnScroll
          hideScrollThreshold={70}
        />

        <main role="main">
          <Hero t={t} currentLanguage={currentLanguage} />
          <Features t={t} />
          <Demo t={t} />
          <WhatsNew t={t} />
          <Milestones t={t} />
          <AboutUs t={t} />
          <SocialProof t={t} />
          <FAQ t={t} />
        </main>

        <footer className="footer" role="contentinfo">
          <div className="container">
            <p>{t.footer.copyright}</p>
          </div>
        </footer>

        <AnimatePresence>
          {showWaitlist && (
            <Waitlist
              t={t}
              isVisible={showWaitlist}
              onClose={() => setShowWaitlist(false)}
            />
          )}
        </AnimatePresence>

        {showQuizModal && (
          <div className="waitlist-overlay" role="dialog" aria-modal="true" aria-label="Daily Quiz">
            <div className="quiz-modal">
              <button className="close-button" aria-label="Close" onClick={() => setShowQuizModal(false)}>×</button>
              <div className="quiz-modal-content">
                <DailyQuiz />
              </div>
            </div>
          </div>
        )}
      </div>
    </HelmetProvider>
  );
}

export default App; 
